import {
  collection,
  deleteDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  doc,
  limit,
  where
} from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { db, functions, httpsCallable, storage } from '../firebase';
import { getPreinvoiceRecipients, previewPreinvoiceMessage } from './preinvoiceUtils';

const SEND_INVOICES_FN = httpsCallable(functions, 'sendInvoices');

export const INVOICE_STATUSES = ['Sent', 'pending payment', 'Payed', 'sendError'];

export const SELECTABLE_PAYMENT_STATUSES = ['Sent', 'pending payment', 'Payed'];

export const INVOICE_EMAIL_FROM_NAME = 'TenisoUžsiėmimai.lt';

export const INVOICE_DEFAULT_SERVICE = '85.51.00 - Sportinis rekreacinis švietimas';

// Keep in sync with INVOICE_DEFAULT_MESSAGE_TEMPLATE in functions/index.js.
export const INVOICE_DEFAULT_MESSAGE_TEMPLATE = `Laba diena, {{vardas}},

Siunčiame sąskaitą faktūrą už suteiktas paslaugas. PDF failas laiško prisegtuke. Taip pat sąskaitą galite peržiūrėti paspaudę nuorodos mygtuką apačioje.

Suma: {{suma}}
Sąskaitos numeris: {{saskaitosNumeris}}
Paslaugų teikimo laikotarpis: {{paslauguLaikotarpis}}

{{pdfUrl}}

Linkime malonaus laiko aikštelėje!
${INVOICE_EMAIL_FROM_NAME}`;

export const INVOICE_TEMPLATE_PLACEHOLDERS = [
  { key: '{{vardas}}', description: 'kliento vardas' },
  { key: '{{pdfUrl}}', description: 'PDF nuoroda (HTML laiške rodoma kaip mygtukas „Atsisiųsti sąskaitą (PDF)")' },
  { key: '{{suma}}', description: 'suma (pvz. „40.00 Eur“)' },
  { key: '{{saskaitosNumeris}}', description: 'sąskaitos numeris' },
  { key: '{{paslauguLaikotarpis}}', description: 'paslaugų teikimo laikotarpis, neprivaloma (pvz. „2026-05-01 – 2026-05-31")' },
  { key: '{{data}}', description: 'metai-mėnuo (pvz. „2026-05“)' }
];


export const omitServicePeriodFromInvoiceTemplate = (template) => (
  String(template || '')
    .split('\n')
    .filter((line) => !/\{\{paslauguLaikotarpis\}\}/.test(line) && !/^Paslaugų teikimo laikotarpis:/.test(line.trim()))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
);

export const formatInvoiceServicePeriodLabel = (startDate, endDate) => {
  if (!startDate || !endDate) return '';
  if (startDate === endDate) return startDate;
  return `${startDate} – ${endDate}`;
};

export { getPreinvoiceRecipients as getInvoiceRecipients, previewPreinvoiceMessage as previewInvoiceMessage };

export const sendInvoices = async ({
  amountEur,
  serviceDescription,
  recipients,
  isTest,
  messageTemplate,
  servicePeriodStart,
  servicePeriodEnd,
  sendEmail = true,
  paymentStatus
}) => {
  const response = await SEND_INVOICES_FN({
    amountEur,
    serviceDescription: serviceDescription || INVOICE_DEFAULT_SERVICE,
    recipients,
    isTest: isTest === true,
    messageTemplate: typeof messageTemplate === 'string' ? messageTemplate : '',
    servicePeriodStart: typeof servicePeriodStart === 'string' ? servicePeriodStart : '',
    servicePeriodEnd: typeof servicePeriodEnd === 'string' ? servicePeriodEnd : '',
    sendEmail: sendEmail !== false,
    paymentStatus: SELECTABLE_PAYMENT_STATUSES.includes(paymentStatus) ? paymentStatus : 'Payed'
  });
  return response?.data || { ok: false, results: [] };
};

export const subscribeToRecentInvoices = (year, callback) => {
  const yearRef = collection(db, 'invoices', String(year), 'items');
  const q = query(yearRef, orderBy('createdAt', 'desc'), limit(100));
  return onSnapshot(q, (snap) => {
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(list);
  }, (error) => {
    console.error('invoices snapshot error:', error);
    callback([]);
  });
};

/** Client view: invoices assigned to this user (by email or auth uid). */
export const subscribeToClientInvoices = (year, clientEmail, clientUid, callback) => {
  const normalizedEmail = String(clientEmail || '').trim().toLowerCase();
  if (!normalizedEmail && !clientUid) {
    callback([]);
    return () => {};
  }
  const yearRef = collection(db, 'invoices', String(year), 'items');
  const q = normalizedEmail
    ? query(yearRef, where('clientEmail', '==', normalizedEmail), orderBy('createdAt', 'desc'), limit(100))
    : query(yearRef, where('clientId', '==', clientUid), orderBy('createdAt', 'desc'), limit(100));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, (error) => {
    console.error('client invoices snapshot error:', error);
    callback([]);
  });
};

export const updateInvoiceStatus = async (year, docId, status) => {
  if (!INVOICE_STATUSES.includes(status)) {
    throw new Error('Neteisingas statusas');
  }
  const itemRef = doc(db, 'invoices', String(year), 'items', docId);
  await updateDoc(itemRef, { status });
};

export const deleteInvoiceEntry = async (year, item) => {
  const docId = item?.id;
  if (!docId) {
    throw new Error('Trūksta sąskaitos įrašo ID');
  }

  const tasks = [];

  if (item?.pdfStoragePath) {
    tasks.push(
      deleteObject(ref(storage, item.pdfStoragePath)).catch((error) => {
        if (error?.code !== 'storage/object-not-found') {
          console.warn('Skipping PDF delete for invoice:', error?.code || error?.message || error);
        }
      })
    );
  }

  tasks.push(deleteDoc(doc(db, 'invoices', String(year), 'items', docId)));

  const mailQ = query(collection(db, 'mail'), where('reservationId', '==', docId));
  const mailSnap = await getDocs(mailQ);
  mailSnap.forEach((mailDoc) => {
    tasks.push(deleteDoc(mailDoc.ref));
  });

  await Promise.all(tasks);
};
